import { BaseComponent } from '../../components/base-component'
import { RentalIntentResponse, RentalPeriod } from '../../types/rental'

import { ConfiguratorProduct } from './configurator-product'

export class NeckwareType {
  private element: HTMLElement
  private _products: ConfiguratorProduct[] = []
  private onProductSelected: (productId: string, variantId: number) => void

  constructor(
    element: HTMLElement,
    onProductSelected: (productId: string, variantId: number) => void
  ) {
    this.element = element
    this.onProductSelected = onProductSelected
    this.initializeProducts()
  }

  private initializeProducts() {
    const productElements = this.element.querySelectorAll('.product-configurator__step-product')
    productElements.forEach(element => {
      this._products.push(new ConfiguratorProduct(
        element as HTMLElement,
        this.handleProductSelected.bind(this)
      ))
    })
  }

  private handleProductSelected(productId: string, variantId: number) {
    console.log('NeckwareType handleProductSelected', productId, variantId)
    this._products.forEach(product => {
      if (product.productId !== productId) {
        product.disable()
      }
    })
    this.onProductSelected(productId, variantId)
  }

  show() {
    this.element.classList.add('product-configurator__step-products--active')
  }

  hide() {
    this.element.classList.remove('product-configurator__step-products--active')
  }

  updateAvailableVariants(productId: string, availableVariantIds: number[]) {
    const product = this._products.find(p => p.productId === productId)
    product?.updateAvailableVariants(availableVariantIds)
  }

  reset() {
    this._products.forEach(product => product.reset())
    this.hide()
  }

  get products(): ConfiguratorProduct[] {
    return this._products
  }
}


export class NeckwareStep extends BaseComponent {
  private neckwareTypes: Map<string, NeckwareType> = new Map()
  private typeSelect: HTMLElement | null = null
  private nextButton: HTMLButtonElement | null = null
  private selectedType: string | null = null
  private selectedProductId: string | null = null
  private selectedVariantId: number | null = null
  private hasCreatedRentalIntent: boolean = false
  private rentalIntentToken: RentalIntentResponse | null = null
  private rentalIntentTokens: Map<string, string> = new Map()
  private _products: ConfiguratorProduct[] = []

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.initializeElements()
    this.initializeListeners()
  }

  private initializeElements() {
    this.typeSelect = this.querySelector('.product-configurator__select--neckware')
    this.nextButton = this.querySelector('.product-configurator__step-footer-button')

    // Initialize neckware type components
    const typeElements = this.querySelectorAll('.product-configurator__step-products--neckware')
    typeElements.forEach(element => {
      const type = (element as HTMLElement).dataset.neckwareType
      if (type) {
        this.neckwareTypes.set(type, new NeckwareType(
          element as HTMLElement,
          this.handleProductSelected.bind(this)
        ))
      }
    })
  }

  private initializeListeners() {
    // Type select trigger
    const trigger = this.typeSelect?.querySelector('.product-configurator__select-trigger')
    trigger?.addEventListener('click', (e) => {
      e.stopPropagation()
      this.typeSelect?.classList.toggle('product-configurator__select--active')
    })

    // Type select items
    const items = this.typeSelect?.querySelectorAll('.product-configurator__select-list-item')
    items?.forEach(item => {
      item.addEventListener('click', (e) => this.handleTypeSelect(e))
    })

    // Close select on outside click
    document.addEventListener('click', (e) => {
      if (!(e.target as HTMLElement).closest('.product-configurator__select')) {
        const activeSelects = this.querySelectorAll('.product-configurator__select--active')
        activeSelects.forEach(select => {
          select.classList.remove('product-configurator__select--active')
        })
      }
    })
  }

  private handleTypeSelect(event: Event) {
    const item = event.target as HTMLElement
    const type = item.dataset.value
    if (!type) return

    // Update select text
    const triggerText = this.typeSelect?.querySelector('.product-configurator__select-trigger-text')
    if (triggerText) {
      triggerText.textContent = item.textContent
    }

    // Hide previous type if any
    if (this.selectedType) {
      this.neckwareTypes.get(this.selectedType)?.hide()
    }

    // Show selected type
    this.selectedType = type
    this.neckwareTypes.get(type)?.show()

    // Close select
    this.typeSelect?.classList.remove('product-configurator__select--active')
  }

  private handleProductSelected(productId: string, variantId: number) {
    console.log('NeckwareStep handleProductSelected', {
      productId,
      variantId,
      selectedType: this.selectedType
    });
    this.selectedProductId = productId;
    this.selectedVariantId = variantId;

    if (this.nextButton) {
      this.nextButton.disabled = false;
      this.nextButton.classList.add('product-configurator__step-footer-button--active');
    }
  }

  activate() {
    this.classList.add('product-configurator__step--active')
  }

  deactivate() {
    this.classList.remove('product-configurator__step--active')
  }

  complete() {
    this.classList.add('product-configurator__step--completed')
  }

  getIndex(): number {
    return parseInt(this.dataset.index || '-1')
  }

  isValid(): boolean {
    return this.selectedVariantId !== null
  }

  getCurrentVariantId(): number | null {
    console.log('NeckwareStep getCurrentVariantId called:', this.selectedVariantId);
    return this.selectedVariantId;
  }

  hasRentalIntent(): boolean {
    return this.hasCreatedRentalIntent
  }

  getRentalPeriodByDuration(duration: number): RentalPeriod | null {
    if (!this.selectedType || !this.selectedProductId) return null
    
    const selectedType = this.neckwareTypes.get(this.selectedType)
    if (!selectedType) return null

    const product = selectedType['products'].find(p => p.productId === this.selectedProductId)
    return product?.getRentalPeriodByDuration(duration) || null
  }

  getRentalIntentToken(productId: string): string {
    return this.rentalIntentTokens.get(productId) || '';
  }

  setRentalIntentToken(response: RentalIntentResponse, productId: string) {
    this.rentalIntentTokens.set(productId, JSON.stringify(response));
  }

  getAllRentalIntentTokens(): Map<string, string> {
    return new Map(this.rentalIntentTokens);
  }

  updateAvailableVariants(productId: string, availableVariantIds: number[]) {
    this.neckwareTypes.forEach(type => {
      type.updateAvailableVariants(productId, availableVariantIds)
    })
  }

  reset() {
    this.selectedType = null
    this.selectedProductId = null
    this.selectedVariantId = null
    this.hasCreatedRentalIntent = false
    this.rentalIntentToken = null
    this.classList.remove('product-configurator__step--completed')
    
    // Reset type selector
    const typeSelector = this.querySelector('.product-configurator__select-trigger-text')
    if (typeSelector) typeSelector.textContent = 'Choose a type'
    
    // Reset all products
    this.neckwareTypes.forEach(type => {
        type.reset()
        type.hide() // Ensure all product groups are hidden
    })
    
    // Reset skip state - make sure the step is skippable again
    if (this.dataset.optional === 'true') {
        const skipButton = this.querySelector('.product-configurator__step-skip-button') as HTMLButtonElement
        if (skipButton) {
            skipButton.disabled = false
            skipButton.style.display = 'block' // or whatever the original display value should be
        }
    }
    
    // Disable next button
    const nextButton = this.querySelector('.product-configurator__step-footer-button') as HTMLButtonElement
    if (nextButton) nextButton.disabled = true
  }

  getProductDetails() {
    if (!this.selectedProductId) return []
    
    const type = this.selectedType
    const product = this.neckwareTypes.get(type || '')?.products
      .find(p => p.productId === this.selectedProductId)

    if (!product) return []

    return [{
      productId: this.selectedProductId,
      title: product.element.dataset.productTitle || '',
      options: product.getSelectedOptions() || {}
    }]
  }

  get products(): ConfiguratorProduct[] {
    return this._products
  }

  getSelectedProductId(): string | null {
    return this.selectedProductId
  }

  setHasCreatedRentalIntent(value: boolean) {
    this.hasCreatedRentalIntent = value;
  }
}
